import { FaMobileAlt, FaCloudShowersHeavy, FaWifi, FaLeaf, FaTools, FaCheckCircle, FaFileDownload, FaSeedling, FaShieldAlt, FaHeartbeat, FaDollarSign, FaFlask, FaBolt, FaLaptop } from 'react-icons/fa';
import { MdSolarPower, MdAutoMode, MdWarning } from 'react-icons/md';
import { BsLightningChargeFill } from 'react-icons/bs';
import UserGuidePDF from '../assets/User-Guide.pdf';
import CropShieldAPK from '../assets/CropShield.apk';

/**
 * Language-aware static content.
 *
 * All translatable copy lives in the TEXT map below, keyed by language.
 * Non-translatable data (icons, links, image sources, ratings, avatars and
 * numeric stat values) lives in the SHARED map and is merged in by language.
 *
 * NOTE: The product name "CropShield" is intentionally NOT translated — it is
 * a brand name and must read identically in every language.
 */

// ---------------------------------------------------------------------------
// Shared, non-translatable data (icons / links / images / numbers)
// ---------------------------------------------------------------------------
const SHARED = {
  hero: { ctaIcon: FaLeaf },
  features: [MdSolarPower, FaWifi, BsLightningChargeFill, FaMobileAlt, FaCloudShowersHeavy, MdAutoMode],
  why: {
    problemIcon: MdWarning,
    purposeIcons: [FaSeedling, FaShieldAlt],
    purposeTypes: ['primary', 'secondary'],
    valueIcons: [FaHeartbeat, FaDollarSign, FaFlask, FaShieldAlt],
  },
  usage: {
    stepImages: [FaTools, BsLightningChargeFill, FaMobileAlt, FaCheckCircle],
    stepNumbers: ['01', '02', '03', '04'],
  },
  downloads: {
    icons: [FaMobileAlt, FaFileDownload, FaSeedling],
    links: [CropShieldAPK, UserGuidePDF, 'preset'],
  },
  testimonials: [
    { rating: 5, avatar: 'https://api.dicebear.com/7.x/thumbs/svg?seed=Ramesh&backgroundColor=d1fae5' },
    { rating: 5, avatar: 'https://api.dicebear.com/7.x/thumbs/svg?seed=Suresh&backgroundColor=d1fae5' },
    { rating: 4, avatar: 'https://api.dicebear.com/7.x/thumbs/svg?seed=Meena&backgroundColor=d1fae5' },
    { rating: 5, avatar: 'https://api.dicebear.com/7.x/thumbs/svg?seed=Arjun&backgroundColor=d1fae5' },
    { rating: 5, avatar: 'https://api.dicebear.com/7.x/thumbs/svg?seed=Kavita&backgroundColor=d1fae5' },
    { rating: 4, avatar: 'https://api.dicebear.com/7.x/thumbs/svg?seed=Bhavesh&backgroundColor=d1fae5' },
  ],
  about: {
    statValues: ['Wide', '100%', '24/7', '₹0'],
    expertiseIcons: [FaBolt, FaTools, FaLaptop],
  },
};

// ---------------------------------------------------------------------------
// Translatable copy, keyed by language
// ---------------------------------------------------------------------------
const TEXT = {
  // =========================================================================
  // ENGLISH
  // =========================================================================
  en: {
    nav: {
      menu: ['Home', 'Why CropShield', 'Gallery', 'How to Use', 'Downloads', 'About Us', 'Crop Presets'],
    },
    hero: {
      label: 'Solar-Powered Crop Protection',
      heading: 'Protect Your Crops with CropShield',
      description: 'Solar-powered automatic insect control device with 400-600m coverage. UV LED technology, mobile app monitoring, and eco-friendly operation.',
      ctaText: 'Get Started',
    },
    features: {
      sectionLabel: 'Advantageous Features',
      sectionTitle: 'Features Never Stop',
      eyebrow: 'Core Features',
      items: [
        { title: 'Solar-Powered', description: 'Advanced solar charging during the day with 100% eco-friendly renewable energy. Zero electricity bills, maximum sustainability.' },
        { title: 'Wide Coverage', description: 'Protect 400-600m radius on flat, open land. Single device covers multiple acres, ensuring comprehensive crop protection.' },
        { title: 'UV Technology', description: 'Powerful UV and warm light flashes combined with 3KV high-voltage zapper for immediate and effective insect elimination.' },
        { title: 'Mobile App Control', description: 'Real-time monitoring and remote control via smartphone app. Track voltage, battery status, and configure settings anywhere, anytime.' },
        { title: 'Rain Protection', description: 'Intelligent rain detection sensor automatically protects the device during wet conditions, ensuring long-lasting durability.' },
        { title: 'Autonomous Operation', description: 'Smart sunset detection automatically activates night operation. Pre-configure crop-specific settings for complete hands-free protection.' },
      ],
    },
    why: {
      sectionLabel: 'The Purpose',
      sectionTitle: 'Why CropShield Exists',
      description: 'Solving the root cause, not treating the symptoms',
      valuesHeading: 'Why This Solution Works',
      problem: {
        title: "The Problem We're Solving",
        description: 'Widespread pesticide use makes vegetables and grains toxic, contributing to serious health problems. Farmers face rising pesticide costs, health concerns, and lack of safe alternatives. We studied the root cause and realized many pests can be controlled without chemicals.',
      },
      purposes: [
        { title: 'Primary Purpose: Farming', description: 'Protect crops from insects during their active periods without chemical pesticides. Studied crop-specific insect behavior to create a field-deployable solution that saves farmers money while keeping produce safe and healthy.', highlights: ['Chemical-Free Crop Protection', 'Targets Active Pest Periods', 'Reduces Pesticide Costs'] },
        { title: 'Secondary Purpose: Mosquito Control', description: 'Eliminate disease-causing mosquitos in agricultural and residential areas. Provides dual benefit by protecting both crops and human health from mosquito-borne diseases like malaria, dengue, and zika.', highlights: ['Disease Prevention', 'Safe for Humans', 'Residential Protection'] },
      ],
      values: [
        { title: 'Health First', description: 'Eliminate toxic pesticides from your food chain. Protect farmer health and consumer safety.' },
        { title: 'Cost Effective', description: 'Zero electricity bills with solar power. Dramatically reduce expensive chemical pesticide costs.' },
        { title: 'Scientifically Designed', description: 'Built on deep domain expertise in hardware, electronics, and software. Field-tested solution.' },
        { title: 'Dual Protection', description: 'Protect crops and eliminate mosquitos simultaneously. One device, two critical purposes.' },
      ],
    },
    usage: {
      sectionLabel: 'How to Use',
      sectionTitle: 'Simple Setup, Powerful Protection',
      description: 'Get started in minutes with our easy installation process',
      packageHeading: 'Package Includes',
      ctaText: 'Get Started Now',
      steps: [
        { title: 'Install the Device', description: 'Attach solar panel, mount on pole at least 2 feet above crop height. Connect all components.' },
        { title: 'Power On', description: 'Turn on the switch. Device performs self-test and is ready for operation.' },
        { title: 'Connect App', description: 'Download app, connect via WiFi hotspot, and configure your preferred settings.' },
        { title: 'Automatic Protection', description: 'Device automatically activates at sunset. Monitor performance through mobile app.' },
      ],
      packageIncludes: ['1 x CropShield Device', '1 x Solar Panel', '1 x Hanging Clamp', '3 x Clamp Bolts', '1 x Cleaning Brush', '1 x User Guide'],
    },
    downloads: {
      sectionLabel: 'Get Started',
      sectionTitle: 'Download & Explore',
      description: 'Everything you need to get the most out of your CropShield device',
      helpTitle: 'Need Help Getting Started?',
      helpDescription: 'Download our comprehensive user guide for detailed installation instructions, troubleshooting tips, and app configuration help.',
      contactSupport: 'Contact Support',
      items: [
        { title: 'Mobile App', description: 'Monitor and control your device from anywhere', buttonText: 'Download App' },
        { title: 'User Guide', description: 'Complete installation and operation manual', buttonText: 'Download PDF' },
        { title: 'Crop Presets', description: 'Browse crop-specific settings and recommendations', buttonText: 'View Presets' },
      ],
    },
    testimonials: {
      sectionLabel: 'User Reviews',
      sectionTitle: 'What Farmers Say',
      items: [
        { name: 'Ramesh Patel', location: 'Anand, Gujarat', review: 'CropShield has completely changed how I protect my cotton crop. No more expensive pesticides and my yield has improved significantly this season.' },
        { name: 'Suresh Kumar', location: 'Pune, Maharashtra', review: 'The solar-powered device works all night without any electricity cost. The mobile app makes it very easy to monitor everything remotely.' },
        { name: 'Meena Devi', location: 'Jaipur, Rajasthan', review: 'My vegetable farm is now free from harmful insects. The device covers a large area and my family feels safe eating our own produce.' },
        { name: 'Arjun Singh', location: 'Ludhiana, Punjab', review: 'Best investment for my wheat farm. Setup was simple and the automatic sunset activation means I never have to worry about turning it on.' },
        { name: 'Kavita Sharma', location: 'Nashik, Maharashtra', review: 'I grow grapes and the UV technology has drastically reduced pest damage. My export quality has improved and buyers are very happy.' },
        { name: 'Bhavesh Desai', location: 'Surat, Gujarat', review: 'Very durable device. It survived the monsoon season without any issues. The rain sensor protection feature really works as advertised.' },
      ],
    },
    about: {
      sectionLabel: 'About Us',
      sectionTitle: 'Our Mission',
      description: 'We are committed to revolutionizing agriculture with innovative solar-powered solutions that protect crops while preserving the environment for future generations.',
      vision: 'Building a sustainable future where every farmer has access to advanced, eco-friendly crop protection technology.',
      motiveTitle: 'Our Motive',
      motiveDescription: 'Widespread pesticide use makes vegetables and grains toxic, contributing to serious health problems. We solve the root cause instead of treating symptoms.',
      motiveBullets: ['Eliminate toxic pesticides from food chain', 'Reduce rising pesticide costs for farmers', 'Provide safe alternatives to chemical spraying'],
      expertiseTitle: 'Built with Deep Domain Expertise',
      expertiseDescription: 'Strong expertise across hardware, electronics, and software enabled the design of a practical, field-deployable solution based on studying crop-specific insect behavior.',
      expertise: [
        { label: 'Electronics', desc: 'Advanced circuit design' },
        { label: 'Hardware', desc: 'Field-tested durability' },
        { label: 'Software', desc: 'Smart automation' },
      ],
      impactTitle: 'Real Farmers, Real Results',
      impactDescription: 'Farmers repeatedly told us about rising pesticide costs, health concerns, and lack of safe alternatives. Early field usage of CropShield shows it can protect crops without chemical spraying, delivering on our promise of healthier food and sustainable farming.',
      statLabels: ['Coverage', 'Solar Power', 'Operation', 'Running Cost'],
    },
    gallery: {
      sectionLabel: 'Gallery',
      titleLead: 'See CropShield',
      titleHighlight: 'Up Close',
      description: 'From the workbench to the field — explore the device, real installations, and the results it delivers.',
      categories: [
        { id: 'all', label: 'All Photos' },
        { id: 'device', label: 'The Device' },
        { id: 'field', label: 'In the Field' },
        { id: 'closeup', label: 'Close-Up' },
        { id: 'results', label: 'Results' },
      ],
      captions: {
        inAction: { title: 'CropShield in Action', caption: 'Full system deployed and protecting the field' },
        deviceFront: { title: 'Device View', caption: 'CropShield unit — front view' },
        deviceSide: { title: 'Device View', caption: 'CropShield unit — side profile' },
        farmerFriendly: { title: 'Farmer-Friendly', caption: 'Simple enough for anyone to set up' },
        solarMount: { title: 'Device View', caption: 'Solar panel and mounting assembly' },
        weatherBuild: { title: 'Device View', caption: 'Weather-resistant build quality' },
        results1: { title: 'Proven Results', caption: 'Captured pests after one night of operation' },
        results2: { title: 'Proven Results', caption: 'Effective mosquito and pest elimination' },
        ruggedField: { title: 'Device View', caption: 'Compact, rugged field design' },
        closeTrap: { title: 'Close-Up Detail', caption: 'Precision-engineered trap mechanism' },
        readyInstall: { title: 'Device View', caption: 'Ready for installation' },
        results3: { title: 'Proven Results', caption: 'Real results from real farms' },
        closeUv: { title: 'Close-Up Detail', caption: 'UV attractant light module' },
        fullySolar: { title: 'Device View', caption: 'Fully solar-powered operation' },
        builtToLast: { title: 'Device View', caption: 'Built to last season after season' },
        results4: { title: 'Proven Results', caption: 'Measurable pest reduction' },
      },
    },
    footer: {
      brandDescription: 'Solar-powered crop protection device for sustainable pest control.',
      quickLinksTitle: 'Quick Links',
      quickLinks: ['Home', 'Why CropShield', 'How to Use', 'Downloads', 'About Us'],
      featuresTitle: 'Features',
      featuresItems: ['Solar-Powered Operation', 'Mobile App Monitoring', '400-600m Coverage', 'UV LED Technology'],
      contactTitle: 'Support & Contact',
      privacy: 'Privacy Policy',
      terms: 'Terms & Conditions',
      rights: 'All rights reserved.',
      madeWith: 'Made with',
      forFarmers: 'for Indian Farmers',
      kisanFirst: 'Kisan First',
    },
  },

  // =========================================================================
  // HINDI
  // =========================================================================
  hi: {
    nav: {
      menu: ['होम', 'क्यों CropShield', 'गैलरी', 'उपयोग कैसे करें', 'डाउनलोड', 'हमारे बारे में', 'फसल प्रीसेट'],
    },
    hero: {
      label: 'सौर-संचालित फसल सुरक्षा',
      heading: 'CropShield के साथ अपनी फसलों की रक्षा करें',
      description: '400-600 मीटर कवरेज वाला सौर-संचालित स्वचालित कीट नियंत्रण उपकरण। UV LED तकनीक, मोबाइल ऐप निगरानी और पर्यावरण-अनुकूल संचालन।',
      ctaText: 'शुरू करें',
    },
    features: {
      sectionLabel: 'लाभकारी विशेषताएं',
      sectionTitle: 'विशेषताएं कभी नहीं रुकतीं',
      eyebrow: 'मुख्य विशेषताएं',
      items: [
        { title: 'सौर-संचालित', description: 'दिन के दौरान उन्नत सौर चार्जिंग, 100% पर्यावरण-अनुकूल नवीकरणीय ऊर्जा के साथ। शून्य बिजली बिल, अधिकतम स्थिरता।' },
        { title: 'व्यापक कवरेज', description: 'समतल, खुली भूमि पर 400-600 मीटर त्रिज्या की सुरक्षा। एक ही उपकरण कई एकड़ को कवर करता है, संपूर्ण फसल सुरक्षा सुनिश्चित करता है।' },
        { title: 'UV तकनीक', description: 'शक्तिशाली UV और गर्म प्रकाश की चमक, 3KV उच्च-वोल्टेज ज़ैपर के साथ मिलकर तुरंत और प्रभावी कीट उन्मूलन करती है।' },
        { title: 'मोबाइल ऐप नियंत्रण', description: 'स्मार्टफोन ऐप के माध्यम से रीयल-टाइम निगरानी और रिमोट नियंत्रण। वोल्टेज, बैटरी स्थिति ट्रैक करें और कहीं भी, कभी भी सेटिंग कॉन्फ़िगर करें।' },
        { title: 'वर्षा सुरक्षा', description: 'बुद्धिमान वर्षा पहचान सेंसर गीली परिस्थितियों में उपकरण की स्वचालित रूप से रक्षा करता है, दीर्घकालिक टिकाऊपन सुनिश्चित करता है।' },
        { title: 'स्वायत्त संचालन', description: 'स्मार्ट सूर्यास्त पहचान स्वचालित रूप से रात्रि संचालन सक्रिय करती है। पूर्ण हाथ-मुक्त सुरक्षा के लिए फसल-विशिष्ट सेटिंग पहले से कॉन्फ़िगर करें।' },
      ],
    },
    why: {
      sectionLabel: 'उद्देश्य',
      sectionTitle: 'CropShield क्यों मौजूद है',
      description: 'लक्षणों का इलाज नहीं, मूल कारण का समाधान',
      valuesHeading: 'यह समाधान क्यों काम करता है',
      problem: {
        title: 'जिस समस्या को हम हल कर रहे हैं',
        description: 'व्यापक कीटनाशक उपयोग सब्जियों और अनाज को विषाक्त बना देता है, जिससे गंभीर स्वास्थ्य समस्याएं होती हैं। किसानों को बढ़ती कीटनाशक लागत, स्वास्थ्य चिंताओं और सुरक्षित विकल्पों की कमी का सामना करना पड़ता है। हमने मूल कारण का अध्ययन किया और महसूस किया कि कई कीटों को रसायनों के बिना नियंत्रित किया जा सकता है।',
      },
      purposes: [
        { title: 'प्राथमिक उद्देश्य: खेती', description: 'रासायनिक कीटनाशकों के बिना कीटों की सक्रिय अवधि के दौरान फसलों की रक्षा करें। फसल-विशिष्ट कीट व्यवहार का अध्ययन कर एक खेत में तैनात करने योग्य समाधान बनाया जो किसानों का पैसा बचाता है और उपज को सुरक्षित एवं स्वस्थ रखता है।', highlights: ['रसायन-मुक्त फसल सुरक्षा', 'सक्रिय कीट अवधि पर लक्ष्य', 'कीटनाशक लागत कम करता है'] },
        { title: 'द्वितीयक उद्देश्य: मच्छर नियंत्रण', description: 'कृषि और आवासीय क्षेत्रों में रोग फैलाने वाले मच्छरों को समाप्त करें। मलेरिया, डेंगू और ज़ीका जैसे मच्छर जनित रोगों से फसलों और मानव स्वास्थ्य दोनों की रक्षा कर दोहरा लाभ प्रदान करता है।', highlights: ['रोग निवारण', 'मनुष्यों के लिए सुरक्षित', 'आवासीय सुरक्षा'] },
      ],
      values: [
        { title: 'स्वास्थ्य पहले', description: 'अपनी खाद्य श्रृंखला से विषाक्त कीटनाशकों को हटाएं। किसान स्वास्थ्य और उपभोक्ता सुरक्षा की रक्षा करें।' },
        { title: 'लागत प्रभावी', description: 'सौर ऊर्जा के साथ शून्य बिजली बिल। महंगी रासायनिक कीटनाशक लागत को नाटकीय रूप से कम करें।' },
        { title: 'वैज्ञानिक रूप से डिज़ाइन किया गया', description: 'हार्डवेयर, इलेक्ट्रॉनिक्स और सॉफ्टवेयर में गहन विशेषज्ञता पर निर्मित। खेत में परीक्षण किया गया समाधान।' },
        { title: 'दोहरी सुरक्षा', description: 'एक साथ फसलों की रक्षा करें और मच्छरों को समाप्त करें। एक उपकरण, दो महत्वपूर्ण उद्देश्य।' },
      ],
    },
    usage: {
      sectionLabel: 'उपयोग कैसे करें',
      sectionTitle: 'सरल सेटअप, शक्तिशाली सुरक्षा',
      description: 'हमारी आसान स्थापना प्रक्रिया के साथ मिनटों में शुरू करें',
      packageHeading: 'पैकेज में शामिल है',
      ctaText: 'अभी शुरू करें',
      steps: [
        { title: 'उपकरण स्थापित करें', description: 'सौर पैनल जोड़ें, फसल की ऊंचाई से कम से कम 2 फीट ऊपर खंभे पर लगाएं। सभी घटकों को जोड़ें।' },
        { title: 'चालू करें', description: 'स्विच चालू करें। उपकरण स्व-परीक्षण करता है और संचालन के लिए तैयार है।' },
        { title: 'ऐप कनेक्ट करें', description: 'ऐप डाउनलोड करें, WiFi हॉटस्पॉट के माध्यम से कनेक्ट करें और अपनी पसंदीदा सेटिंग कॉन्फ़िगर करें।' },
        { title: 'स्वचालित सुरक्षा', description: 'उपकरण सूर्यास्त के समय स्वचालित रूप से सक्रिय हो जाता है। मोबाइल ऐप के माध्यम से प्रदर्शन की निगरानी करें।' },
      ],
      packageIncludes: ['1 x CropShield उपकरण', '1 x सौर पैनल', '1 x लटकाने वाला क्लैंप', '3 x क्लैंप बोल्ट', '1 x सफाई ब्रश', '1 x उपयोगकर्ता गाइड'],
    },
    downloads: {
      sectionLabel: 'शुरू करें',
      sectionTitle: 'डाउनलोड करें और खोजें',
      description: 'अपने CropShield उपकरण का अधिकतम लाभ उठाने के लिए आपको जो कुछ भी चाहिए',
      helpTitle: 'शुरू करने में मदद चाहिए?',
      helpDescription: 'विस्तृत स्थापना निर्देशों, समस्या निवारण युक्तियों और ऐप कॉन्फ़िगरेशन सहायता के लिए हमारी व्यापक उपयोगकर्ता गाइड डाउनलोड करें।',
      contactSupport: 'सहायता से संपर्क करें',
      items: [
        { title: 'मोबाइल ऐप', description: 'कहीं से भी अपने उपकरण की निगरानी और नियंत्रण करें', buttonText: 'ऐप डाउनलोड करें' },
        { title: 'उपयोगकर्ता गाइड', description: 'संपूर्ण स्थापना और संचालन मैनुअल', buttonText: 'PDF डाउनलोड करें' },
        { title: 'फसल प्रीसेट', description: 'फसल-विशिष्ट सेटिंग और सिफारिशें ब्राउज़ करें', buttonText: 'प्रीसेट देखें' },
      ],
    },
    testimonials: {
      sectionLabel: 'उपयोगकर्ता समीक्षाएं',
      sectionTitle: 'किसान क्या कहते हैं',
      items: [
        { name: 'रमेश पटेल', location: 'आनंद, गुजरात', review: 'CropShield ने मेरी कपास की फसल की सुरक्षा का तरीका पूरी तरह बदल दिया है। अब महंगे कीटनाशक नहीं और इस मौसम में मेरी उपज में काफी सुधार हुआ है।' },
        { name: 'सुरेश कुमार', location: 'पुणे, महाराष्ट्र', review: 'सौर-संचालित उपकरण बिना किसी बिजली लागत के पूरी रात काम करता है। मोबाइल ऐप से सब कुछ दूर से निगरानी करना बहुत आसान हो जाता है।' },
        { name: 'मीना देवी', location: 'जयपुर, राजस्थान', review: 'मेरा सब्जी का खेत अब हानिकारक कीटों से मुक्त है। उपकरण बड़े क्षेत्र को कवर करता है और मेरा परिवार अपनी उपज खाने में सुरक्षित महसूस करता है।' },
        { name: 'अर्जुन सिंह', location: 'लुधियाना, पंजाब', review: 'मेरे गेहूं के खेत के लिए सबसे अच्छा निवेश। सेटअप सरल था और स्वचालित सूर्यास्त सक्रियण का मतलब है कि मुझे इसे चालू करने की चिंता कभी नहीं करनी पड़ती।' },
        { name: 'कविता शर्मा', location: 'नासिक, महाराष्ट्र', review: 'मैं अंगूर उगाती हूं और UV तकनीक ने कीट क्षति को काफी कम कर दिया है। मेरी निर्यात गुणवत्ता में सुधार हुआ है और खरीदार बहुत खुश हैं।' },
        { name: 'भावेश देसाई', location: 'सूरत, गुजरात', review: 'बहुत टिकाऊ उपकरण। यह बिना किसी समस्या के मानसून के मौसम से बच गया। वर्षा सेंसर सुरक्षा सुविधा वास्तव में विज्ञापन के अनुसार काम करती है।' },
      ],
    },
    about: {
      sectionLabel: 'हमारे बारे में',
      sectionTitle: 'हमारा मिशन',
      description: 'हम नवीन सौर-संचालित समाधानों के साथ कृषि में क्रांति लाने के लिए प्रतिबद्ध हैं जो फसलों की रक्षा करते हैं और आने वाली पीढ़ियों के लिए पर्यावरण को संरक्षित करते हैं।',
      vision: 'एक स्थायी भविष्य का निर्माण जहां हर किसान की पहुंच उन्नत, पर्यावरण-अनुकूल फसल सुरक्षा तकनीक तक हो।',
      motiveTitle: 'हमारा उद्देश्य',
      motiveDescription: 'व्यापक कीटनाशक उपयोग सब्जियों और अनाज को विषाक्त बना देता है, जिससे गंभीर स्वास्थ्य समस्याएं होती हैं। हम लक्षणों का इलाज करने के बजाय मूल कारण का समाधान करते हैं।',
      motiveBullets: ['खाद्य श्रृंखला से विषाक्त कीटनाशकों को हटाएं', 'किसानों के लिए बढ़ती कीटनाशक लागत को कम करें', 'रासायनिक छिड़काव के सुरक्षित विकल्प प्रदान करें'],
      expertiseTitle: 'गहन डोमेन विशेषज्ञता के साथ निर्मित',
      expertiseDescription: 'हार्डवेयर, इलेक्ट्रॉनिक्स और सॉफ्टवेयर में मजबूत विशेषज्ञता ने फसल-विशिष्ट कीट व्यवहार के अध्ययन पर आधारित एक व्यावहारिक, खेत में तैनात करने योग्य समाधान के डिज़ाइन को संभव बनाया।',
      expertise: [
        { label: 'इलेक्ट्रॉनिक्स', desc: 'उन्नत सर्किट डिज़ाइन' },
        { label: 'हार्डवेयर', desc: 'खेत में परीक्षित टिकाऊपन' },
        { label: 'सॉफ्टवेयर', desc: 'स्मार्ट स्वचालन' },
      ],
      impactTitle: 'असली किसान, असली परिणाम',
      impactDescription: 'किसानों ने बार-बार हमें बढ़ती कीटनाशक लागत, स्वास्थ्य चिंताओं और सुरक्षित विकल्पों की कमी के बारे में बताया। CropShield का प्रारंभिक क्षेत्र उपयोग दिखाता है कि यह रासायनिक छिड़काव के बिना फसलों की रक्षा कर सकता है, जो स्वस्थ भोजन और टिकाऊ खेती के हमारे वादे को पूरा करता है।',
      statLabels: ['कवरेज', 'सौर ऊर्जा', 'संचालन', 'चलने की लागत'],
    },
    gallery: {
      sectionLabel: 'गैलरी',
      titleLead: 'CropShield को',
      titleHighlight: 'करीब से देखें',
      description: 'वर्कबेंच से खेत तक — उपकरण, वास्तविक स्थापनाओं और इसके परिणामों का अन्वेषण करें।',
      categories: [
        { id: 'all', label: 'सभी तस्वीरें' },
        { id: 'device', label: 'उपकरण' },
        { id: 'field', label: 'खेत में' },
        { id: 'closeup', label: 'नज़दीक से' },
        { id: 'results', label: 'परिणाम' },
      ],
      captions: {
        inAction: { title: 'CropShield कार्य में', caption: 'पूरी प्रणाली तैनात और खेत की सुरक्षा करती हुई' },
        deviceFront: { title: 'उपकरण दृश्य', caption: 'CropShield इकाई — सामने का दृश्य' },
        deviceSide: { title: 'उपकरण दृश्य', caption: 'CropShield इकाई — बगल का प्रोफ़ाइल' },
        farmerFriendly: { title: 'किसान-अनुकूल', caption: 'किसी के भी सेट करने के लिए काफी सरल' },
        solarMount: { title: 'उपकरण दृश्य', caption: 'सौर पैनल और माउंटिंग असेंबली' },
        weatherBuild: { title: 'उपकरण दृश्य', caption: 'मौसम-प्रतिरोधी निर्माण गुणवत्ता' },
        results1: { title: 'सिद्ध परिणाम', caption: 'एक रात के संचालन के बाद पकड़े गए कीट' },
        results2: { title: 'सिद्ध परिणाम', caption: 'प्रभावी मच्छर और कीट उन्मूलन' },
        ruggedField: { title: 'उपकरण दृश्य', caption: 'कॉम्पैक्ट, मजबूत खेत डिज़ाइन' },
        closeTrap: { title: 'नज़दीकी विवरण', caption: 'सटीक-इंजीनियर्ड ट्रैप तंत्र' },
        readyInstall: { title: 'उपकरण दृश्य', caption: 'स्थापना के लिए तैयार' },
        results3: { title: 'सिद्ध परिणाम', caption: 'असली खेतों से असली परिणाम' },
        closeUv: { title: 'नज़दीकी विवरण', caption: 'UV आकर्षक प्रकाश मॉड्यूल' },
        fullySolar: { title: 'उपकरण दृश्य', caption: 'पूरी तरह सौर-संचालित संचालन' },
        builtToLast: { title: 'उपकरण दृश्य', caption: 'मौसम दर मौसम टिकने के लिए बनाया गया' },
        results4: { title: 'सिद्ध परिणाम', caption: 'मापनीय कीट कमी' },
      },
    },
    footer: {
      brandDescription: 'सतत कीट नियंत्रण के लिए सौर-संचालित फसल सुरक्षा उपकरण।',
      quickLinksTitle: 'त्वरित लिंक',
      quickLinks: ['होम', 'क्यों CropShield', 'उपयोग कैसे करें', 'डाउनलोड', 'हमारे बारे में'],
      featuresTitle: 'विशेषताएं',
      featuresItems: ['सौर-संचालित संचालन', 'मोबाइल ऐप निगरानी', '400-600 मीटर कवरेज', 'UV LED तकनीक'],
      contactTitle: 'सहायता और संपर्क',
      privacy: 'गोपनीयता नीति',
      terms: 'नियम और शर्तें',
      rights: 'सर्वाधिकार सुरक्षित।',
      madeWith: 'बनाया गया',
      forFarmers: 'भारतीय किसानों के लिए',
      kisanFirst: 'किसान पहले',
    },
  },

  // =========================================================================
  // GUJARATI
  // =========================================================================
  gu: {
    nav: {
      menu: ['હોમ', 'શા માટે CropShield', 'ગેલેરી', 'ઉપયોગ કેવી રીતે કરવો', 'ડાઉનલોડ', 'અમારા વિશે', 'પાક પ્રીસેટ'],
    },
    hero: {
      label: 'સૌર-સંચાલિત પાક સુરક્ષા',
      heading: 'CropShield સાથે તમારા પાકનું રક્ષણ કરો',
      description: '400-600 મીટર કવરેજ સાથે સૌર-સંચાલિત સ્વચાલિત જંતુ નિયંત્રણ ઉપકરણ. UV LED ટેકનોલોજી, મોબાઇલ એપ મોનિટરિંગ અને પર્યાવરણ-અનુકૂળ સંચાલન.',
      ctaText: 'શરૂ કરો',
    },
    features: {
      sectionLabel: 'લાભદાયી વિશેષતાઓ',
      sectionTitle: 'વિશેષતાઓ ક્યારેય અટકતી નથી',
      eyebrow: 'મુખ્ય વિશેષતાઓ',
      items: [
        { title: 'સૌર-સંચાલિત', description: 'દિવસ દરમિયાન અદ્યતન સૌર ચાર્જિંગ, 100% પર્યાવરણ-અનુકૂળ નવીકરણીય ઊર્જા સાથે. શૂન્ય વીજળી બિલ, મહત્તમ ટકાઉપણું.' },
        { title: 'વ્યાપક કવરેજ', description: 'સપાટ, ખુલ્લી જમીન પર 400-600 મીટર ત્રિજ્યાનું રક્ષણ. એક જ ઉપકરણ અનેક એકર આવરી લે છે, સંપૂર્ણ પાક સુરક્ષા સુનિશ્ચિત કરે છે.' },
        { title: 'UV ટેકનોલોજી', description: 'શક્તિશાળી UV અને ગરમ પ્રકાશની ઝલક, 3KV ઉચ્ચ-વોલ્ટેજ ઝેપર સાથે મળીને તાત્કાલિક અને અસરકારક જંતુ નાબૂદી કરે છે.' },
        { title: 'મોબાઇલ એપ નિયંત્રણ', description: 'સ્માર્ટફોન એપ દ્વારા રીઅલ-ટાઇમ મોનિટરિંગ અને રિમોટ નિયંત્રણ. વોલ્ટેજ, બેટરી સ્થિતિ ટ્રૅક કરો અને ગમે ત્યાં, ગમે ત્યારે સેટિંગ ગોઠવો.' },
        { title: 'વરસાદ સુરક્ષા', description: 'બુદ્ધિશાળી વરસાદ શોધ સેન્સર ભીની પરિસ્થિતિઓમાં ઉપકરણનું આપોઆપ રક્ષણ કરે છે, લાંબા સમય સુધી ટકાઉપણું સુનિશ્ચિત કરે છે.' },
        { title: 'સ્વાયત્ત સંચાલન', description: 'સ્માર્ટ સૂર્યાસ્ત શોધ આપોઆપ રાત્રિ સંચાલન સક્રિય કરે છે. સંપૂર્ણ હાથ-મુક્ત સુરક્ષા માટે પાક-વિશિષ્ટ સેટિંગ પહેલેથી ગોઠવો.' },
      ],
    },
    why: {
      sectionLabel: 'હેતુ',
      sectionTitle: 'CropShield શા માટે અસ્તિત્વમાં છે',
      description: 'લક્ષણોની સારવાર નહીં, મૂળ કારણનું નિરાકરણ',
      valuesHeading: 'આ ઉકેલ શા માટે કામ કરે છે',
      problem: {
        title: 'જે સમસ્યા અમે હલ કરી રહ્યા છીએ',
        description: 'વ્યાપક જંતુનાશક ઉપયોગ શાકભાજી અને અનાજને ઝેરી બનાવે છે, જે ગંભીર આરોગ્ય સમસ્યાઓમાં ફાળો આપે છે. ખેડૂતોને વધતી જંતુનાશક કિંમત, આરોગ્ય ચિંતાઓ અને સલામત વિકલ્પોના અભાવનો સામનો કરવો પડે છે. અમે મૂળ કારણનો અભ્યાસ કર્યો અને સમજ્યું કે ઘણા જંતુઓને રસાયણ વિના નિયંત્રિત કરી શકાય છે.',
      },
      purposes: [
        { title: 'પ્રાથમિક હેતુ: ખેતી', description: 'રાસાયણિક જંતુનાશકો વિના જંતુઓના સક્રિય સમયગાળા દરમિયાન પાકનું રક્ષણ કરો. પાક-વિશિષ્ટ જંતુ વર્તનનો અભ્યાસ કરીને ખેતરમાં તૈનાત કરી શકાય તેવો ઉકેલ બનાવ્યો જે ખેડૂતોના પૈસા બચાવે છે અને ઉપજને સલામત તથા સ્વસ્થ રાખે છે.', highlights: ['રસાયણ-મુક્ત પાક સુરક્ષા', 'સક્રિય જંતુ સમયગાળા પર લક્ષ્ય', 'જંતુનાશક ખર્ચ ઘટાડે છે'] },
        { title: 'ગૌણ હેતુ: મચ્છર નિયંત્રણ', description: 'કૃષિ અને રહેણાંક વિસ્તારોમાં રોગ ફેલાવતા મચ્છરોને દૂર કરો. મેલેરિયા, ડેન્ગ્યુ અને ઝીકા જેવા મચ્છરજન્ય રોગોથી પાક અને માનવ આરોગ્ય બંનેનું રક્ષણ કરીને બમણો લાભ આપે છે.', highlights: ['રોગ નિવારણ', 'મનુષ્યો માટે સલામત', 'રહેણાંક સુરક્ષા'] },
      ],
      values: [
        { title: 'આરોગ્ય પ્રથમ', description: 'તમારી ખાદ્ય શૃંખલામાંથી ઝેરી જંતુનાશકો દૂર કરો. ખેડૂત આરોગ્ય અને ગ્રાહક સલામતીનું રક્ષણ કરો.' },
        { title: 'ખર્ચ અસરકારક', description: 'સૌર ઊર્જા સાથે શૂન્ય વીજળી બિલ. મોંઘા રાસાયણિક જંતુનાશક ખર્ચને નાટકીય રીતે ઘટાડો.' },
        { title: 'વૈજ્ઞાનિક રીતે ડિઝાઇન કરેલ', description: 'હાર્ડવેર, ઇલેક્ટ્રોનિક્સ અને સોફ્ટવેરમાં ઊંડી નિપુણતા પર બનેલ. ખેતરમાં પરીક્ષણ કરેલ ઉકેલ.' },
        { title: 'બમણી સુરક્ષા', description: 'એકસાથે પાકનું રક્ષણ કરો અને મચ્છરોને દૂર કરો. એક ઉપકરણ, બે મહત્વપૂર્ણ હેતુઓ.' },
      ],
    },
    usage: {
      sectionLabel: 'ઉપયોગ કેવી રીતે કરવો',
      sectionTitle: 'સરળ સેટઅપ, શક્તિશાળી સુરક્ષા',
      description: 'અમારી સરળ સ્થાપના પ્રક્રિયા સાથે મિનિટોમાં શરૂ કરો',
      packageHeading: 'પેકેજમાં સમાવેશ થાય છે',
      ctaText: 'હમણાં શરૂ કરો',
      steps: [
        { title: 'ઉપકરણ સ્થાપિત કરો', description: 'સૌર પેનલ જોડો, પાકની ઊંચાઈથી ઓછામાં ઓછા 2 ફૂટ ઉપર થાંભલા પર માઉન્ટ કરો. બધા ઘટકો જોડો.' },
        { title: 'ચાલુ કરો', description: 'સ્વિચ ચાલુ કરો. ઉપકરણ સ્વ-પરીક્ષણ કરે છે અને સંચાલન માટે તૈયાર છે.' },
        { title: 'એપ કનેક્ટ કરો', description: 'એપ ડાઉનલોડ કરો, WiFi હોટસ્પોટ દ્વારા કનેક્ટ કરો અને તમારી પસંદગીની સેટિંગ ગોઠવો.' },
        { title: 'સ્વચાલિત સુરક્ષા', description: 'ઉપકરણ સૂર્યાસ્ત સમયે આપોઆપ સક્રિય થાય છે. મોબાઇલ એપ દ્વારા કામગીરીનું નિરીક્ષણ કરો.' },
      ],
      packageIncludes: ['1 x CropShield ઉપકરણ', '1 x સૌર પેનલ', '1 x લટકાવવાનું ક્લેમ્પ', '3 x ક્લેમ્પ બોલ્ટ', '1 x સફાઈ બ્રશ', '1 x વપરાશકર્તા માર્ગદર્શિકા'],
    },
    downloads: {
      sectionLabel: 'શરૂ કરો',
      sectionTitle: 'ડાઉનલોડ કરો અને શોધો',
      description: 'તમારા CropShield ઉપકરણનો મહત્તમ લાભ મેળવવા માટે તમને જે જોઈએ તે બધું',
      helpTitle: 'શરૂ કરવામાં મદદ જોઈએ છે?',
      helpDescription: 'વિગતવાર સ્થાપના સૂચનાઓ, મુશ્કેલી નિવારણ ટીપ્સ અને એપ ગોઠવણી સહાય માટે અમારી વ્યાપક વપરાશકર્તા માર્ગદર્શિકા ડાઉનલોડ કરો.',
      contactSupport: 'સહાયનો સંપર્ક કરો',
      items: [
        { title: 'મોબાઇલ એપ', description: 'ગમે ત્યાંથી તમારા ઉપકરણનું નિરીક્ષણ અને નિયંત્રણ કરો', buttonText: 'એપ ડાઉનલોડ કરો' },
        { title: 'વપરાશકર્તા માર્ગદર્શિકા', description: 'સંપૂર્ણ સ્થાપના અને સંચાલન માર્ગદર્શિકા', buttonText: 'PDF ડાઉનલોડ કરો' },
        { title: 'પાક પ્રીસેટ', description: 'પાક-વિશિષ્ટ સેટિંગ અને ભલામણો બ્રાઉઝ કરો', buttonText: 'પ્રીસેટ જુઓ' },
      ],
    },
    testimonials: {
      sectionLabel: 'વપરાશકર્તા સમીક્ષાઓ',
      sectionTitle: 'ખેડૂતો શું કહે છે',
      items: [
        { name: 'રમેશ પટેલ', location: 'આણંદ, ગુજરાત', review: 'CropShield એ મારા કપાસના પાકનું રક્ષણ કરવાની રીત સંપૂર્ણપણે બદલી નાખી છે. હવે મોંઘા જંતુનાશકો નહીં અને આ સિઝનમાં મારી ઉપજમાં નોંધપાત્ર સુધારો થયો છે.' },
        { name: 'સુરેશ કુમાર', location: 'પુણે, મહારાષ્ટ્ર', review: 'સૌર-સંચાલિત ઉપકરણ કોઈપણ વીજળી ખર્ચ વિના આખી રાત કામ કરે છે. મોબાઇલ એપ દરેક વસ્તુને દૂરથી મોનિટર કરવાનું ખૂબ સરળ બનાવે છે.' },
        { name: 'મીના દેવી', location: 'જયપુર, રાજસ્થાન', review: 'મારું શાકભાજીનું ખેતર હવે હાનિકારક જંતુઓથી મુક્ત છે. ઉપકરણ મોટા વિસ્તારને આવરી લે છે અને મારો પરિવાર અમારી પોતાની ઉપજ ખાવામાં સલામત અનુભવે છે.' },
        { name: 'અર્જુન સિંહ', location: 'લુધિયાણા, પંજાબ', review: 'મારા ઘઉંના ખેતર માટે શ્રેષ્ઠ રોકાણ. સેટઅપ સરળ હતું અને સ્વચાલિત સૂર્યાસ્ત સક્રિયકરણનો અર્થ છે કે મારે તેને ચાલુ કરવાની ક્યારેય ચિંતા કરવી પડતી નથી.' },
        { name: 'કવિતા શર્મા', location: 'નાસિક, મહારાષ્ટ્ર', review: 'હું દ્રાક્ષ ઉગાડું છું અને UV ટેકનોલોજીએ જંતુ નુકસાનને ભારે ઘટાડ્યું છે. મારી નિકાસ ગુણવત્તા સુધરી છે અને ખરીદદારો ખૂબ ખુશ છે.' },
        { name: 'ભાવેશ દેસાઈ', location: 'સુરત, ગુજરાત', review: 'ખૂબ ટકાઉ ઉપકરણ. તે કોઈપણ સમસ્યા વિના ચોમાસાની સિઝનમાં ટકી ગયું. વરસાદ સેન્સર સુરક્ષા સુવિધા ખરેખર જાહેરાત મુજબ કામ કરે છે.' },
      ],
    },
    about: {
      sectionLabel: 'અમારા વિશે',
      sectionTitle: 'અમારું મિશન',
      description: 'અમે નવીન સૌર-સંચાલિત ઉકેલો સાથે કૃષિમાં ક્રાંતિ લાવવા માટે પ્રતિબદ્ધ છીએ જે પાકનું રક્ષણ કરે છે અને ભાવિ પેઢીઓ માટે પર્યાવરણને સાચવે છે.',
      vision: 'એક ટકાઉ ભવિષ્યનું નિર્માણ જ્યાં દરેક ખેડૂતને અદ્યતન, પર્યાવરણ-અનુકૂળ પાક સુરક્ષા ટેકનોલોજીની પહોંચ હોય.',
      motiveTitle: 'અમારો હેતુ',
      motiveDescription: 'વ્યાપક જંતુનાશક ઉપયોગ શાકભાજી અને અનાજને ઝેરી બનાવે છે, જે ગંભીર આરોગ્ય સમસ્યાઓમાં ફાળો આપે છે. અમે લક્ષણોની સારવાર કરવાને બદલે મૂળ કારણનું નિરાકરણ કરીએ છીએ.',
      motiveBullets: ['ખાદ્ય શૃંખલામાંથી ઝેરી જંતુનાશકો દૂર કરો', 'ખેડૂતો માટે વધતી જંતુનાશક કિંમત ઘટાડો', 'રાસાયણિક છંટકાવના સલામત વિકલ્પો પ્રદાન કરો'],
      expertiseTitle: 'ઊંડી ડોમેન નિપુણતા સાથે બનેલ',
      expertiseDescription: 'હાર્ડવેર, ઇલેક્ટ્રોનિક્સ અને સોફ્ટવેરમાં મજબૂત નિપુણતાએ પાક-વિશિષ્ટ જંતુ વર્તનના અભ્યાસ પર આધારિત વ્યવહારુ, ખેતરમાં તૈનાત કરી શકાય તેવા ઉકેલની ડિઝાઇનને શક્ય બનાવી.',
      expertise: [
        { label: 'ઇલેક્ટ્રોનિક્સ', desc: 'અદ્યતન સર્કિટ ડિઝાઇન' },
        { label: 'હાર્ડવેર', desc: 'ખેતરમાં પરીક્ષિત ટકાઉપણું' },
        { label: 'સોફ્ટવેર', desc: 'સ્માર્ટ સ્વચાલન' },
      ],
      impactTitle: 'સાચા ખેડૂતો, સાચા પરિણામો',
      impactDescription: 'ખેડૂતોએ વારંવાર અમને વધતી જંતુનાશક કિંમત, આરોગ્ય ચિંતાઓ અને સલામત વિકલ્પોના અભાવ વિશે જણાવ્યું. CropShield નો પ્રારંભિક ક્ષેત્ર ઉપયોગ દર્શાવે છે કે તે રાસાયણિક છંટકાવ વિના પાકનું રક્ષણ કરી શકે છે, જે સ્વસ્થ ખોરાક અને ટકાઉ ખેતીના અમારા વચનને પૂરું કરે છે.',
      statLabels: ['કવરેજ', 'સૌર ઊર્જા', 'સંચાલન', 'ચાલવાનો ખર્ચ'],
    },
    gallery: {
      sectionLabel: 'ગેલેરી',
      titleLead: 'CropShield ને',
      titleHighlight: 'નજીકથી જુઓ',
      description: 'વર્કબેન્ચથી ખેતર સુધી — ઉપકરણ, વાસ્તવિક સ્થાપનાઓ અને તે આપતા પરિણામોનું અન્વેષણ કરો.',
      categories: [
        { id: 'all', label: 'બધા ફોટા' },
        { id: 'device', label: 'ઉપકરણ' },
        { id: 'field', label: 'ખેતરમાં' },
        { id: 'closeup', label: 'નજીકથી' },
        { id: 'results', label: 'પરિણામો' },
      ],
      captions: {
        inAction: { title: 'CropShield કાર્યમાં', caption: 'સંપૂર્ણ સિસ્ટમ તૈનાત અને ખેતરનું રક્ષણ કરતી' },
        deviceFront: { title: 'ઉપકરણ દૃશ્ય', caption: 'CropShield એકમ — આગળનું દૃશ્ય' },
        deviceSide: { title: 'ઉપકરણ દૃશ્ય', caption: 'CropShield એકમ — બાજુની પ્રોફાઇલ' },
        farmerFriendly: { title: 'ખેડૂત-અનુકૂળ', caption: 'કોઈપણ માટે સેટ કરવા માટે પૂરતું સરળ' },
        solarMount: { title: 'ઉપકરણ દૃશ્ય', caption: 'સૌર પેનલ અને માઉન્ટિંગ એસેમ્બલી' },
        weatherBuild: { title: 'ઉપકરણ દૃશ્ય', caption: 'હવામાન-પ્રતિરોધક બાંધકામ ગુણવત્તા' },
        results1: { title: 'સાબિત પરિણામો', caption: 'એક રાતના સંચાલન પછી પકડાયેલા જંતુઓ' },
        results2: { title: 'સાબિત પરિણામો', caption: 'અસરકારક મચ્છર અને જંતુ નાબૂદી' },
        ruggedField: { title: 'ઉપકરણ દૃશ્ય', caption: 'કોમ્પેક્ટ, મજબૂત ખેતર ડિઝાઇન' },
        closeTrap: { title: 'નજીકની વિગત', caption: 'ચોકસાઇથી બનેલ ટ્રેપ મિકેનિઝમ' },
        readyInstall: { title: 'ઉપકરણ દૃશ્ય', caption: 'સ્થાપના માટે તૈયાર' },
        results3: { title: 'સાબિત પરિણામો', caption: 'સાચા ખેતરોમાંથી સાચા પરિણામો' },
        closeUv: { title: 'નજીકની વિગત', caption: 'UV આકર્ષક પ્રકાશ મોડ્યુલ' },
        fullySolar: { title: 'ઉપકરણ દૃશ્ય', caption: 'સંપૂર્ણ સૌર-સંચાલિત સંચાલન' },
        builtToLast: { title: 'ઉપકરણ દૃશ્ય', caption: 'સિઝન દર સિઝન ટકવા માટે બનેલ' },
        results4: { title: 'સાબિત પરિણામો', caption: 'માપી શકાય તેવો જંતુ ઘટાડો' },
      },
    },
    footer: {
      brandDescription: 'ટકાઉ જંતુ નિયંત્રણ માટે સૌર-સંચાલિત પાક સુરક્ષા ઉપકરણ.',
      quickLinksTitle: 'ઝડપી લિંક્સ',
      quickLinks: ['હોમ', 'શા માટે CropShield', 'ઉપયોગ કેવી રીતે કરવો', 'ડાઉનલોડ', 'અમારા વિશે'],
      featuresTitle: 'વિશેષતાઓ',
      featuresItems: ['સૌર-સંચાલિત સંચાલન', 'મોબાઇલ એપ મોનિટરિંગ', '400-600 મીટર કવરેજ', 'UV LED ટેકનોલોજી'],
      contactTitle: 'સહાય અને સંપર્ક',
      privacy: 'ગોપનીયતા નીતિ',
      terms: 'નિયમો અને શરતો',
      rights: 'સર્વાધિકાર સુરક્ષિત.',
      madeWith: 'બનાવ્યું',
      forFarmers: 'ભારતીય ખેડૂતો માટે',
      kisanFirst: 'ખેડૂત પ્રથમ',
    },
  },
};

// ---------------------------------------------------------------------------
// Public API — build the fully-resolved content object for a language
// ---------------------------------------------------------------------------
export const getStaticContent = (language = 'en') => {
  const t = TEXT[language] || TEXT.en;

  return {
    NAV: {
      menu: t.nav.menu,
    },
    HERO_CONTENT: {
      label: t.hero.label,
      heading: t.hero.heading,
      description: t.hero.description,
      ctaText: t.hero.ctaText,
      ctaIcon: SHARED.hero.ctaIcon,
    },
    FEATURES_CONTENT: {
      sectionLabel: t.features.sectionLabel,
      sectionTitle: t.features.sectionTitle,
      eyebrow: t.features.eyebrow,
      features: SHARED.features.map((icon, i) => ({ icon, ...t.features.items[i] })),
    },
    WHY_CONTENT: {
      sectionLabel: t.why.sectionLabel,
      sectionTitle: t.why.sectionTitle,
      description: t.why.description,
      valuesHeading: t.why.valuesHeading,
      problem: { ...t.why.problem, icon: SHARED.why.problemIcon },
      purposes: t.why.purposes.map((p, i) => ({
        type: SHARED.why.purposeTypes[i],
        icon: SHARED.why.purposeIcons[i],
        ...p,
      })),
      values: t.why.values.map((v, i) => ({ icon: SHARED.why.valueIcons[i], ...v })),
    },
    USAGE_CONTENT: {
      sectionLabel: t.usage.sectionLabel,
      sectionTitle: t.usage.sectionTitle,
      description: t.usage.description,
      packageHeading: t.usage.packageHeading,
      ctaText: t.usage.ctaText,
      steps: t.usage.steps.map((s, i) => ({
        stepNumber: SHARED.usage.stepNumbers[i],
        image: SHARED.usage.stepImages[i],
        ...s,
      })),
      packageIncludes: t.usage.packageIncludes,
    },
    DOWNLOADS_CONTENT: {
      sectionLabel: t.downloads.sectionLabel,
      sectionTitle: t.downloads.sectionTitle,
      description: t.downloads.description,
      helpTitle: t.downloads.helpTitle,
      helpDescription: t.downloads.helpDescription,
      contactSupport: t.downloads.contactSupport,
      downloads: t.downloads.items.map((d, i) => ({
        icon: SHARED.downloads.icons[i],
        link: SHARED.downloads.links[i],
        ...d,
      })),
    },
    TESTIMONIALS_CONTENT: {
      sectionLabel: t.testimonials.sectionLabel,
      sectionTitle: t.testimonials.sectionTitle,
      testimonials: t.testimonials.items.map((item, i) => ({ ...SHARED.testimonials[i], ...item })),
    },
    ABOUT_SIMPLE: {
      sectionLabel: t.about.sectionLabel,
      sectionTitle: t.about.sectionTitle,
      description: t.about.description,
      vision: t.about.vision,
      motiveTitle: t.about.motiveTitle,
      motiveDescription: t.about.motiveDescription,
      motiveBullets: t.about.motiveBullets,
      expertiseTitle: t.about.expertiseTitle,
      expertiseDescription: t.about.expertiseDescription,
      expertise: t.about.expertise.map((e, i) => ({ icon: SHARED.about.expertiseIcons[i], ...e })),
      impactTitle: t.about.impactTitle,
      impactDescription: t.about.impactDescription,
      stats: SHARED.about.statValues.map((value, i) => ({ value, label: t.about.statLabels[i] })),
    },
    GALLERY_CONTENT: {
      sectionLabel: t.gallery.sectionLabel,
      titleLead: t.gallery.titleLead,
      titleHighlight: t.gallery.titleHighlight,
      description: t.gallery.description,
      categories: t.gallery.categories,
      captions: t.gallery.captions,
    },
    FOOTER_CONTENT: {
      brandDescription: t.footer.brandDescription,
      quickLinksTitle: t.footer.quickLinksTitle,
      quickLinks: t.footer.quickLinks,
      featuresTitle: t.footer.featuresTitle,
      featuresItems: t.footer.featuresItems,
      contactTitle: t.footer.contactTitle,
      privacy: t.footer.privacy,
      terms: t.footer.terms,
      rights: t.footer.rights,
      madeWith: t.footer.madeWith,
      forFarmers: t.footer.forFarmers,
      kisanFirst: t.footer.kisanFirst,
    },
  };
};
